import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMobileAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FormikProps, Form } from 'formik'
import request from 'superagent'
import './index.css'
import { signatureStatus } from 'src/services/authMandat'

export enum ESecurityMethods {
  SMS = 'otp_sms',
  EMAIL = 'otp_email',
}

interface SignatureMethodChoiceProps {
  onReset?: () => void
}

const SignatureMethodChoice: React.FC<SignatureMethodChoiceProps & FormikProps<any>> = ({
  values,
  setFieldValue,
  handleSubmit,
  onReset,
}) => {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const [iframeLoading, setIframeLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [messageError, setErrorMessage] = useState('')
  const [signatureRequestId, setSignatureRequestId] = useState<string | null>(null)

  const handleChoice = (method: ESecurityMethods) => {
    setFieldValue('securityMethod', method)
  }

  const handleConfirm = async () => {
    if (!values) {
      console.error('Les informations du mandat ou du client sont manquantes.')
      return
    }

    const rawDate = new Date()
    const formattedDate = {
      day: String(rawDate.getDate()).padStart(2, '0'),
      month: String(rawDate.getMonth() + 1).padStart(2, '0'),
      year: String(rawDate.getFullYear()),
    }

    setLoading(true)
    setIframeUrl(null)

    const toSend = {
      firstname: values.clientInfo?.firstname,
      lastname: values.clientInfo?.lastname,
      clientId: values.clientInfo?.num_client,
      address: values.clientInfo?.adresse,
      addressNumber: '0',
      zipCode: values.clientInfo?.codePostal,
      email: values.clientInfo?.email,
      phone: `+${values.clientInfo?.mobile}`,
      city: values.clientInfo?.localite,
      iban: values.clientInfo?.iban,
      bic: values.clientInfo?.bic,
      signe: values.clientInfo?.localite,
      date: formattedDate,
      optionSignature: values.securityMethod,
    }

    try {
      const response = await request.post(`${process.env.REACT_APP_API_URL}/sepa-signature`).send(toSend)
      const signatureLink = response.body?.datas?.youSign?.urlProcedure?.signature

      if (signatureLink) {
        setIframeLoading(true)
        setTimeout(() => {
          setIframeUrl(signatureLink)
        }, 2000)
        const regex = signatureLink?.match(/signatures\/([a-f0-9-]+)\?/)
        const signatureRequestId = regex ? regex[1] : null
        if (signatureRequestId) {
          setSignatureRequestId(signatureRequestId)
        }
      } else {
        console.error('Lien de signature introuvable')
      }
    } catch (error: any) {
      const invalidParams = error?.response?.body?.details?.response?.data?.invalid_params
      const backendMessage = error?.response?.body?.details?.detail
      const genericMessage = error?.response?.body?.error || 'Une erreur inconnue est survenue.'

      if (invalidParams && Array.isArray(invalidParams)) {
        const formattedErrors = invalidParams.map((param: any) => `${param.name}: ${param.reason}`).join('\n')
        setErrorMessage(`Erreur dans les paramètres envoyés :\n${formattedErrors}`)
      } else if (backendMessage) {
        setErrorMessage(backendMessage)
      } else {
        setErrorMessage(genericMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (onReset) onReset()
  }

  const onSubmit = async () => {
    if (!signatureRequestId) return
    try {
      const response = await signatureStatus(signatureRequestId)
      if (response.status === 'done') {
        setErrorMessage('')
        handleSubmit()
      } else {
        setErrorMessage('Veuillez signer le document pour pouvoir continuer.')
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de la signature', error)
    }
  }

  return (
    <div>
      <Form id="inscription">
        <div className="w-full flex items-center justify-center">
          <div className="w-9/12 border-2 border-gray-500 flex items-center justify-center">
            <h3 className="text-xl">Finalisation de la demande</h3>
          </div>
        </div>
        {!iframeUrl && (
          <div className={`flex flex-col items-center justify-center w-full transition-opacity duration-500 ease-in-out
            ${iframeUrl ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100' }`}>
          <h2 className="flex items-center justify-center">Méthode de sécurité</h2>
          <p className="text-gray-600 mb-8">Choisissez votre méthode de sécurité pour signer votre contrat</p>

          <div className="flex gap-6 mb-8">
            <div
              className={`securityChoice ${
                values.securityMethod === ESecurityMethods.SMS ? 'securityChoiceSelected' : 'securityChoiceUnselected'
              }`}
              onClick={() => handleChoice(ESecurityMethods.SMS)}
            >
              <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: '1.5rem' }} />
              <p className="securityChoiceText">SMS</p>
              <span
                className={
                  values.securityMethod === ESecurityMethods.SMS ? 'securitySelectedDot' : 'securityUnselectedDot'
                }
              ></span>
            </div>
            <div
              className={`securityChoice ${
                values.securityMethod === ESecurityMethods.EMAIL ? 'securityChoiceSelected' : 'securityChoiceUnselected'
              }`}
              onClick={() => handleChoice(ESecurityMethods.EMAIL)}
            >
              <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '1.5rem' }} />
              <p className="securityChoiceText">EMAIL</p>
              <span
                className={
                  values.securityMethod === ESecurityMethods.EMAIL ? 'securitySelectedDot' : 'securityUnselectedDot'
                }
              ></span>
            </div>
          </div>

          <button
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded"
            type="button"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Chargement...' : 'Confirmer'}
          </button>
          </div>
        )}
      </Form>

      {loading && (
        <div className="flex justify-center items-center h-[400px]">
          <p className='text-center'>Chargement du lien de signature...</p>
        </div>
      )}

      {iframeUrl && (
        <div className="my-5 w-full max-w-4xl mx-auto">
          {iframeLoading && (
            <div className="flex justify-center items-center h-[800px]">
              <p>Chargement de Yousign en cours...</p>
            </div>
          )}
          <iframe
            src={iframeUrl}
            width="100%"
            height="800px"
            style={{
              border: 'none',
              borderRadius: '8px',
              display: iframeLoading ? 'none' : 'block',
            }}
            onLoad={() => setIframeLoading(false)}
          />
        </div>
      )}

      {values.securityMethod && iframeUrl && (
        <div>
          {messageError && <div style={{ color: 'rgb(210, 38, 48)', textAlign: 'center' }}>{messageError}</div>}
          <div className="w-full flex flex-col mb-5 md:flex-row justify-center items-center">
            <button
              className="cta-mandat-reset mr-5 flex-grow-0"
              style={{ color: 'black', width: '280px' }}
              type="button"
              onClick={handleReset}
            >
              Recommencer la procédure
            </button>
            <button
              style={{ width: '260px', position: 'relative' }}
              className="cta-sign-choice ml-5 flex-grow-0"
              type="submit"
              onClick={onSubmit}
            >
              Valider et Continuer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SignatureMethodChoice
